import React, { useState, useEffect } from 'react';
import { QRCodeCanvas } from 'qrcode.react';

import AppBrandLogo from '../../assets/App20_New.png';
import BgPattern from '../../assets/app20_bg_pattern_v2.png';

const DesktopBlocker: React.FC = () => {
    const [isDesktop, setIsDesktop] = useState(() => {
        if (typeof window !== 'undefined') {
            const userAgent = window.navigator.userAgent.toLowerCase();
            const isIOS = /iphone|ipad|ipod/.test(userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
            return !isIOS;
        }
        return false;
    });

    useEffect(() => {
        const checkScreenSize = () => {
            const userAgent = window.navigator.userAgent.toLowerCase();
            const isIOS = /iphone|ipad|ipod/.test(userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

            // If NOT iOS, we consider it "Desktop/Android" i.e. blocked for this iOS-only app
            setIsDesktop(!isIOS);

            // Lock/Unlock scroll based on blocked state
            if (!isIOS) {
                document.body.style.overflow = 'hidden';
            } else {
                document.body.style.overflow = '';
            }
        };

        // Check immediately on mount (in case resize happened or initial state needs scroll lock)
        checkScreenSize();

        window.addEventListener('resize', checkScreenSize);
        return () => {
            window.removeEventListener('resize', checkScreenSize);
            document.body.style.overflow = ''; // Cleanup
        };
    }, []);

    if (!isDesktop) return null;

    return (
        <div
            className="fixed inset-0 z-[99999] flex flex-col items-center justify-center p-6 transition-all duration-300 bg-[#f0fdf4]"
        >
            {/* Background Pattern with Fade */}
            <div
                className="absolute inset-0 z-0 pointer-events-none opacity-80"
                style={{
                    backgroundImage: `url(${BgPattern})`,
                    backgroundRepeat: 'repeat',
                    backgroundSize: '350px',
                    maskImage: 'linear-gradient(to bottom, black 40%, transparent 100%)',
                    WebkitMaskImage: 'linear-gradient(to bottom, black 40%, transparent 100%)'
                }}
            />
            {/* Subtle Overlay */}
            <div className="absolute inset-0 bg-white/40 backdrop-blur-[1px]" />

            <div className="relative bg-white/90 backdrop-blur-xl p-10 rounded-3xl shadow-[0_20px_50px_-12px_rgba(0,0,0,0.1)] max-w-[480px] w-full border border-white/60 animate-in fade-in zoom-in duration-500">

                {/* Logo Area */}
                <div className="mb-6 flex justify-center">
                    <div className="transform hover:scale-105 transition-transform duration-300">
                        <img
                            src={AppBrandLogo}
                            alt="App20 Logo"
                            className="h-40 w-auto object-contain"
                        />
                    </div>
                </div>

                <h2 className="text-3xl font-bold text-gray-900 mb-4 tracking-tight">
                    iOS Device Required
                </h2>

                <p className="text-gray-600 mb-10 text-lg leading-relaxed font-medium">
                    This application is designed specifically for iOS devices. Please open it on your iPhone or iPad.
                </p>

                {/* QR Code */}
                <div className="relative group w-fit mx-auto">
                    <div className="absolute -inset-1 bg-gradient-to-tr from-blue-400 to-green-400 rounded-2xl blur opacity-25 group-hover:opacity-50 transition duration-500"></div>
                    <div className="relative bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
                        <QRCodeCanvas
                            value="https://app20.in/"
                            size={220}
                            level="H"
                            includeMargin={true}
                            fgColor="#111827"
                        />
                    </div>
                </div>

                <p className="mt-8 text-sm text-gray-500 font-semibold uppercase tracking-widest flex items-center justify-center gap-2">
                    <span className="relative flex h-3 w-3">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                    </span>
                    Scan with your camera
                </p>

            </div>

            <div className="absolute bottom-8 text-gray-600/80 text-xs font-semibold tracking-wide backdrop-blur-md px-4 py-2 rounded-full border border-white/20 bg-white/20">
                &copy; {new Date().getFullYear()} App20. All rights reserved.
            </div>
        </div>
    );
};

export default DesktopBlocker;
